import React, { useMemo, useState } from 'react';
import { Input } from '@fluentui/react-components';
import styles from './WorkspacePulse.module.css';

function initials(name, email) {
    const source = (name || email || '').trim();
    if (!source) return '?';
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return source.slice(0, 2).toUpperCase();
}

const WorkspacePulseRecipientsModal = ({ open, loading, saving, members, recipients, readOnly = false, onClose, onSave }) => {
    const [draftIds, setDraftIds] = useState([]);
    const [search, setSearch] = useState('');

    React.useEffect(() => {
        if (!open) return;
        setDraftIds((recipients || []).map(row => String(row.user_id)));
        setSearch('');
    }, [open, recipients]);

    const draftRecipients = useMemo(() => {
        const byId = new Map((members || []).map(member => [String(member.user_id), member]));
        return draftIds
            .map(id => {
                const member = byId.get(id);
                const persisted = (recipients || []).find(row => String(row.user_id) === id);
                return {
                    user_id: id,
                    full_name: member?.full_name || persisted?.full_name || persisted?.name || id,
                    email: member?.email || persisted?.email || '',
                };
            })
            .filter(row => row.email || row.full_name);
    }, [draftIds, members, recipients]);

    const displayRecipients = readOnly
        ? (recipients || []).map(row => ({
              user_id: String(row.user_id),
              full_name: row.full_name || row.name || row.email,
              email: row.email || '',
          }))
        : draftRecipients;

    const originalIds = useMemo(
        () => [...(recipients || []).map(row => String(row.user_id))].sort(),
        [recipients],
    );
    const hasChanges = JSON.stringify([...draftIds].sort()) !== JSON.stringify(originalIds);

    const searchResults = useMemo(() => {
        if (readOnly) return [];
        const query = search.trim().toLowerCase();
        if (query.length < 2) return [];
        const selected = new Set(draftIds);
        return (members || [])
            .filter(member => member.is_active !== false)
            .filter(member => !selected.has(String(member.user_id)))
            .filter(member => {
                const blob = `${member.full_name || ''} ${member.email || ''}`.toLowerCase();
                return blob.includes(query);
            })
            .slice(0, 8);
    }, [members, search, draftIds, readOnly]);

    if (!open) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose} role="presentation">
            <div className={styles.modal} onClick={event => event.stopPropagation()} role="dialog" aria-modal="true">
                <h2 className={styles.modalTitle}>Workspace Pulse Email Recipients</h2>
                <p className={styles.modalSubtitle}>
                    {readOnly
                        ? 'People invited by a workspace admin to view and receive Workspace Pulse.'
                        : 'Manage who can view and receive Workspace Pulse by email.'}
                </p>

                <p className={styles.sectionLabel}>Current Recipients</p>
                {loading ? (
                    <p className={styles.modalSubtitle}>Loading recipients…</p>
                ) : displayRecipients.length === 0 ? (
                    <p className={styles.modalSubtitle}>No recipients added yet.</p>
                ) : (
                    displayRecipients.map(row => (
                        <div key={row.user_id} className={styles.recipientRow}>
                            <span className={styles.avatar}>{initials(row.full_name, row.email)}</span>
                            <div className={styles.recipientMeta}>
                                <p className={styles.recipientName}>{row.full_name}</p>
                                <p className={styles.recipientEmail}>{row.email}</p>
                            </div>
                            {!readOnly ? (
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() => setDraftIds(prev => prev.filter(id => id !== row.user_id))}
                                >
                                    Remove
                                </button>
                            ) : null}
                        </div>
                    ))
                )}

                {!readOnly ? (
                    <>
                        <p className={styles.sectionLabel} style={{ marginTop: 20 }}>
                            Add Recipient
                        </p>
                        <Input
                            className={styles.searchInput}
                            value={search}
                            onChange={(e, data) => setSearch(data.value)}
                            placeholder="Search by name or email..."
                        />
                        {searchResults.length > 0 ? (
                            <div className={styles.searchResults}>
                                {searchResults.map(member => (
                                    <button
                                        key={member.user_id}
                                        type="button"
                                        className={styles.searchResultRow}
                                        onClick={() => {
                                            setDraftIds(prev => [...prev, String(member.user_id)]);
                                            setSearch('');
                                        }}
                                    >
                                        <span className={styles.recipientName}>{member.full_name || member.email}</span>
                                        <span className={styles.recipientEmail}>{member.email}</span>
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </>
                ) : null}

                <div className={styles.modalActions}>
                    <button type="button" className={readOnly ? styles.primaryButton : styles.secondaryButton} onClick={onClose}>
                        {readOnly ? 'Close' : 'Cancel'}
                    </button>
                    {!readOnly ? (
                        <button
                            type="button"
                            className={styles.primaryButton}
                            disabled={!hasChanges || saving}
                            onClick={() => onSave(draftIds)}
                        >
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default WorkspacePulseRecipientsModal;
